import { RepeatOutlined as RepeatIcon } from "@mui/icons-material";
import { Box, GridLegacy as Grid } from "@mui/material";
import React, { lazy, Suspense, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { AdminEmptyState } from "@/components/common/AdminEmptyState";
import { LongRunningDataLoader } from "@/components/common/LongRunningDataLoader";
import { getReportNetworkLoading } from "@/hooks/getReportNetworkLoading";
import { useContinuousReadsReport } from "@/hooks/useContinuousReadsReport";
import ContinuousReadsOccurrencesTable from "@/pages/reports/continuous-reads/ContinuousReadsOccurrencesTable";
import { StoreInsightsPanel } from "@/pages/reports/shared/StoreInsightsPanel";
import { StoreInsightsReportHeader } from "@/pages/reports/shared/StoreInsightsReportHeader";
import { StoreInsightsSummaryCardsRow } from "@/pages/reports/shared/StoreInsightsSummaryCardsRow";
import { useStoreInsightsReportFilters } from "@/pages/reports/shared/useStoreInsightsReportFilters";
import {
  CurrencyCode,
  formatCurrencyWithNoDecimals,
  getStoreCurrencyIdentifiers,
} from "@/utils/currency";
import { formatDecimal, formatInteger } from "@/utils/number";

const ContinuousReadsDayChart = lazy(
  () => import("@/pages/reports/continuous-reads/ContinuousReadsDayChart"),
);

const ContinuousReadsReport: React.FC = () => {
  const { t, i18n } = useTranslation();
  const {
    user,
    isCraveUser,
    selectedStores,
    loading,
    period,
    customRange,
    currentDateRange,
    deviceType,
    deviceTypeFilterDisabled,
    customers,
    selectedCustomerIds,
    groups,
    selectedGroups,
    groupCounts,
    filteredStores,
    onCustomerChange,
    onGroupsChange,
    onStoresChange,
    onPeriodChange,
    onCustomRangeChange,
  } = useStoreInsightsReportFilters({
    enableDeviceFilter: false,
    defaultDeviceType: "FITTING_ROOM",
  });
  const currentLanguage = i18n.resolvedLanguage || i18n.language || "en-US";
  const currentLocale = useMemo(() => new Intl.Locale(currentLanguage), [currentLanguage]);
  const report = useContinuousReadsReport(
    selectedStores.length ? selectedStores : null,
    currentDateRange,
    "CHANGING_ROOM",
    currentLanguage,
  );
  const networkLoading = getReportNetworkLoading(loading, report.loading, report.tableLoading);
  const contentLoading = networkLoading;
  const chartLoadingKey = useMemo(
    () =>
      [
        selectedStores.map((store) => store.id).join(","),
        currentDateRange.start.getTime(),
        currentDateRange.end.getTime(),
      ].join("|"),
    [currentDateRange.end, currentDateRange.start, selectedStores],
  );
  const hasReportData = !contentLoading && !report.error && report.data.totalReads > 0;
  const [currencyCode, currencySymbol] = useMemo(
    () => getStoreCurrencyIdentifiers(selectedStores),
    [selectedStores],
  );
  const storeTimezones = useMemo(
    () => Object.fromEntries(selectedStores.map((store) => [store.id, store.timezone])),
    [selectedStores],
  );

  const formatAmount = (value: number) =>
    formatCurrencyWithNoDecimals(
      value,
      currencyCode as CurrencyCode,
      currencySymbol,
      currentLocale,
    );
  const formatAverage = (value: number) =>
    formatDecimal(value, currentLocale, {
      minimumFractionDigits: Number.isInteger(value) ? 0 : 1,
      maximumFractionDigits: 1,
    });

  return (
    <Box sx={{ pb: 4 }}>
      <StoreInsightsReportHeader
        title={t("storeInsights.continuousReadsReport.title")}
        icon={<RepeatIcon />}
        isCraveUser={isCraveUser}
        currentUserRole={user?.role}
        customers={customers}
        selectedCustomerIds={selectedCustomerIds}
        onCustomerChange={onCustomerChange}
        groups={groups}
        selectedGroups={selectedGroups}
        groupCounts={groupCounts}
        onGroupsChange={onGroupsChange}
        filteredStores={filteredStores}
        selectedStores={selectedStores}
        onStoresChange={onStoresChange}
        loading={loading}
        networkLoading={networkLoading}
        period={period}
        currentDateRange={currentDateRange}
        customRange={customRange}
        onPeriodChange={onPeriodChange}
        onCustomRangeChange={onCustomRangeChange}
        deviceTypeFilterEnabled
        deviceTypeFilterDisabled={deviceTypeFilterDisabled}
        selectedDeviceType={deviceType}
        includeAllDeviceOption={false}
      />

      <Box sx={{ p: 2.5, pt: 0, mt: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <StoreInsightsSummaryCardsRow
              title={t("storeInsights.continuousReadsReport.sections.storeActivitySummary")}
              cards={[
                {
                  title: t("storeInsights.continuousReadsReport.cards.totalReads"),
                  value: hasReportData
                    ? formatInteger(report.data.totalReads, currentLocale)
                    : "--",
                  previousText: null,
                  isPositive: false,
                  subtitle: t("storeInsights.continuousReadsReport.cards.totalValueSubtitle", {
                    value: hasReportData ? formatAmount(report.data.totalValue) : "--",
                  }),
                  subtitleLoading: contentLoading,
                  pillText: null,
                },
                {
                  title: t("storeInsights.continuousReadsReport.cards.dailyAverage"),
                  value: hasReportData ? formatAverage(report.data.dailyAverage) : "--",
                  previousText: null,
                  isPositive: false,
                  subtitle: t("storeInsights.continuousReadsReport.cards.dailyAverageSubtitle", {
                    value: hasReportData ? formatAmount(report.data.averageDailyValue) : "--",
                  }),
                  subtitleLoading: contentLoading,
                  pillText: null,
                },
              ]}
            />
          </Grid>

          <Grid item xs={12}>
            <StoreInsightsPanel
              title={t("storeInsights.continuousReadsReport.sections.readsByDay")}
            >
              <Box sx={{ position: "relative", height: 360 }}>
                <Suspense fallback={<Box sx={{ height: "100%" }} />}>
                  <Box
                    sx={{
                      height: "100%",
                      visibility:
                        !contentLoading &&
                        report.data.daySummary.length > 0 &&
                        report.data.totalReads > 0
                          ? "visible"
                          : "hidden",
                    }}
                  >
                    <ContinuousReadsDayChart
                      data={report.data.daySummary}
                      locale={currentLocale}
                      readsLabel={t("storeInsights.continuousReadsReport.fields.continuousReads")}
                    />
                  </Box>
                </Suspense>
                {contentLoading ||
                report.data.daySummary.length === 0 ||
                report.data.totalReads === 0 ? (
                  <Box
                    sx={{
                      position: "absolute",
                      inset: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {contentLoading ? (
                      <LongRunningDataLoader
                        variant="focused"
                        isLoading={true}
                        baseLabels={[
                          t("storeInsights.continuousReadsReport.loading.reads"),
                          t("storeInsights.common.loading.readingActivity"),
                          t("storeInsights.common.loading.groupingResults"),
                          t("storeInsights.common.loading.calculatingInsights"),
                          t("storeInsights.common.loading.preparingResults"),
                        ]}
                        loopingLabels={[
                          t("storeInsights.common.loading.stillWorking"),
                          t("storeInsights.common.loading.thanksForPatience"),
                        ]}
                        estimate={{
                          meanTimePerStoreMs: 7_000,
                          storeCount: selectedStores.length,
                          growth: "logarithmic",
                          logarithmicLinearWeight: 0.18,
                        }}
                        locale={currentLanguage}
                        loadingKey={chartLoadingKey}
                        estimateLabel={(duration) =>
                          t("storeComparison.loading.usuallyUnder", { duration })
                        }
                        containerSx={{
                          position: "absolute",
                          inset: 0,
                          minHeight: 0,
                        }}
                      />
                    ) : report.error ? (
                      <Box sx={{ color: "error.main" }}>
                        {t("storeInsights.continuousReadsReport.errors.failedLoad")}
                      </Box>
                    ) : (
                      <AdminEmptyState
                        icon={<RepeatIcon sx={{ fontSize: 40 }} />}
                        text={t("storeInsights.continuousReadsReport.empty.noReads")}
                      />
                    )}
                  </Box>
                ) : null}
              </Box>
            </StoreInsightsPanel>
          </Grid>

          <Grid item xs={12}>
            <ContinuousReadsOccurrencesTable
              data={report.data.occurrences}
              loading={contentLoading}
              error={
                report.error ? t("storeInsights.continuousReadsReport.errors.failedLoad") : null
              }
              storeTimezones={storeTimezones}
              stores={selectedStores}
              loadingKey={chartLoadingKey}
            />
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default ContinuousReadsReport;
